// The cash flow statement's items, and the Period comparison's rows.

import type { CashflowFlowsResponse, ComparisonLayout } from "@/finance/model/wire.js";
import { api, kept, qk } from "@/platform/client";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

/** Every item of the whole history: the page folds them, so a control
 *  changing asks nothing more. */
export const cashflowFlowsQuery = queryOptions({
  queryKey: qk.cashflow.flows,
  queryFn: () => api.get<CashflowFlowsResponse>("/cashflow/flows"),
  ...kept,
});

export const useCashflowFlows = () => useQuery(cashflowFlowsQuery);

interface ComparisonSetting {
  /** Null until the person edits it: the rows the page ships. */
  layout: ComparisonLayout | null;
}

export const comparisonLayoutQuery = queryOptions({
  queryKey: qk.settings.cashFlowComparison,
  queryFn: () => api.get<ComparisonSetting>("/settings/cash-flow-comparison"),
  ...kept,
});

export const useComparisonLayout = () => useQuery(comparisonLayoutQuery);

/** Saves the rows on every change, showing the change at once and putting
 *  the previous rows back if the write fails. */
export function useSaveComparisonLayout() {
  const qc = useQueryClient();
  const key = qk.settings.cashFlowComparison;
  return useMutation({
    mutationFn: (layout: ComparisonLayout | null) =>
      api.put<ComparisonSetting>("/settings/cash-flow-comparison", { layout }),
    onMutate: async (layout) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<ComparisonSetting>(key);
      qc.setQueryData<ComparisonSetting>(key, { layout });
      return { previous };
    },
    onError: (_err, _layout, context) => {
      if (context?.previous) qc.setQueryData(key, context.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}
